import React, { useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { icons } from '../../assets';
import { GetColor, getStatus } from '../../utils';
import * as s from './styled-drill-down';

// Mapeia o nome da fatia do donut (vindo do recharts) para o rangeKey do
// objeto status do appliance. Ex.: clicar em "alarme/erro" filtra
// appliances com appliance.status.E > 0.
const SLICE_NAME_TO_RANGE_KEY = {
  rodando: 'O',
  atenção: 'W',
  'alarme/erro': 'E',
  desligada: 'OFF',
  desconectada: 'NotConnected',
};

const SLICE_NAME_TO_LABEL = {
  rodando: 'Rodando',
  atenção: 'Atenção',
  'alarme/erro': 'Alarme/Erro',
  desligada: 'Desligada',
  desconectada: 'Sem dados',
};

const BREAKDOWN = [
  { rangeKey: 'O', label: 'rodando' },
  { rangeKey: 'W', label: 'atenção' },
  { rangeKey: 'E', label: 'alarme/erro' },
  { rangeKey: 'OFF', label: 'desligada' },
  { rangeKey: 'NotConnected', label: 'sem dados' },
];

const EquipmentDrillDown = ({ tipo, sliceName, markers, onClose }) => {
  const dispatch = useDispatch();
  const history = useHistory();

  // Fecha com ESC — UX padrao de modais
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  // Helper: extrai o tipo do appliance de varios shapes possiveis
  // que o backend pode retornar. Defensivo porque a forma exata varia
  // entre endpoints (objeto, string, nested em thing).
  const getApplianceTypeName = (appliance) => {
    if (!appliance) return null;
    // appliance.type pode ser string direta
    if (typeof appliance.type === 'string') {
      return appliance.type.toLowerCase();
    }
    // ou objeto com .type/.name
    if (appliance.type && typeof appliance.type === 'object') {
      const t = appliance.type.type || appliance.type.name;
      if (t) return String(t).toLowerCase();
    }
    // Fallback: tipo das things internas (caso saga primeiro-nivel)
    if (Array.isArray(appliance.thing) && appliance.thing[0]) {
      const t = appliance.thing[0].type;
      if (typeof t === 'string') return t.toLowerCase();
      if (t && typeof t === 'object') {
        const inner = t.type || t.name;
        if (inner) return String(inner).toLowerCase();
      }
    }
    return null;
  };

  // Filtra appliances: percorre todos os markers (parques), achata o
  // array de appliances, mantem so os do tipo correspondente (ucha/bomba)
  // e que tenham pelo menos 1 maquina na categoria clicada. Tambem acumula
  // o total de maquinas (TGAs/bombas) naquele status para o subtitulo
  // bater com o numero exibido no donut.
  const { filteredAppliances, totalMachines } = useMemo(() => {
    if (!sliceName || !markers || !markers.length) {
      return { filteredAppliances: [], totalMachines: 0 };
    }
    // 'ucha' ou suas variantes vs 'bomba'/'eba' — comparamos case-
    // insensitive e aceitando sinonimos comuns.
    const targetIsUcha = tipo === 'uchas';
    const matchesTargetType = (appliance) => {
      const t = getApplianceTypeName(appliance);
      if (!t) return false;
      if (targetIsUcha) {
        return t === 'ucha' || t === 'tga' || t.includes('ucha');
      }
      return t === 'bomba' || t === 'eba' || t.includes('bomba');
    };
    const rangeKey = SLICE_NAME_TO_RANGE_KEY[sliceName];

    const list = [];
    let machines = 0;
    markers.forEach((marker) => {
      if (!marker.appliance) return;
      marker.appliance.forEach((appliance) => {
        if (!matchesTargetType(appliance)) return;
        const count = Number(
          appliance.status && appliance.status[rangeKey]
        );
        if (!Number.isFinite(count) || count <= 0) return;
        machines += count;
        list.push({
          ...appliance,
          machinesInStatus: count,
          parqueId: marker.id,
          parqueName: marker.name,
          customerName: marker.customer && marker.customer.name,
          customer: marker.customer,
        });
      });
    });

    return { filteredAppliances: list, totalMachines: machines };
    // getApplianceTypeName e local mas fechado por closure estavel —
    // safe omitir do dep array.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sliceName, markers, tipo]);

  if (!sliceName) return null;

  const sliceColor = GetColor(sliceName);
  const sliceLabel = SLICE_NAME_TO_LABEL[sliceName] || sliceName;
  const equipmentLabel = tipo === 'uchas' ? 'UCHAs' : 'Bombas';
  // Nome da unidade que o donut conta (TGAs ou bombas)
  const machineLabel = tipo === 'uchas' ? 'TGAs' : 'bombas';
  const machinePluralizer = totalMachines === 1 ? machineLabel.replace(/s$/, '') : machineLabel;
  const applianceCount = filteredAppliances.length;
  const appliancePluralizer =
    applianceCount === 1
      ? equipmentLabel.replace(/s$/, '')
      : equipmentLabel;

  const handleCardClick = (appliance) => {
    // Mesmo fluxo do clique no marker do mapa: seleciona parque +
    // navega para /dashboard/customer. O operador chega ja com o parque
    // certo aberto e ve o equipamento clicado destacado.
    const parque = markers.find((m) => m.id === appliance.parqueId);
    if (!parque) {
      onClose();
      return;
    }
    dispatch({
      type: 'SEGUNDO/SET_PARQUE_SELECIONADO',
      parqueSelecionado: parque,
      clienteSelecionado: parque.customer,
    });
    history.push('/dashboard/customer', { ...parque.customer });
    onClose();
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <s.Backdrop onClick={handleBackdropClick}>
      <s.Panel
        role="dialog"
        aria-modal="true"
        aria-labelledby="drilldown-title"
      >
        <s.Header>
          <s.TitleGroup>
            <s.Title id="drilldown-title">
              <s.TitleDot color={sliceColor} />
              {equipmentLabel} em {sliceLabel}
            </s.Title>
            <s.Subtitle>
              {totalMachines} {machinePluralizer}
              {applianceCount > 0 && (
                <>
                  {' '}em {applianceCount} {appliancePluralizer}
                </>
              )}
            </s.Subtitle>
          </s.TitleGroup>
          <s.CloseButton
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            title="Fechar (Esc)"
          >
            ×
          </s.CloseButton>
        </s.Header>

        <s.Body>
          {filteredAppliances.length === 0 ? (
            <s.Empty>
              {/* lucide:bar-chart-2 — substituiu emoji 📊 (line-icon
                  alinhado com padrao X/Twitter, ver CLAUDE.md). */}
              <span className="icon" aria-hidden="true">
                <svg
                  width="32"
                  height="32"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="20" x2="18" y2="10" />
                  <line x1="12" y1="20" x2="12" y2="4" />
                  <line x1="6" y1="20" x2="6" y2="14" />
                </svg>
              </span>
              <span>
                Nenhum {tipo === 'uchas' ? 'UCHA' : 'EBA'} em {sliceLabel.toLowerCase()}{' '}
                no momento.
              </span>
            </s.Empty>
          ) : (
            <s.CardsGrid>
              {filteredAppliances.map((appliance) => {
                const aggregateStatus = getStatus(appliance.status);
                // Numero de maquinas dessa UCHA que estao no status clicado
                // — ex.: se o operador clicou em "alarme/erro", este e o
                // count de TGAs em alarme dentro DESSA UCHA. Soma de todos
                // os cards = numero exibido no donut.
                const machinesInStatusCount = appliance.machinesInStatus;
                return (
                  <s.Card
                    key={`${appliance.parqueId}-${appliance.id}`}
                    type="button"
                    onClick={() => handleCardClick(appliance)}
                    title={`${appliance.description} — ${machinesInStatusCount} ${machineLabel} em ${sliceLabel.toLowerCase()}`}
                  >
                    <s.CardHeader>
                      {aggregateStatus && (
                        <s.StatusPill color={GetColor(aggregateStatus)}>
                          {aggregateStatus}
                        </s.StatusPill>
                      )}
                      <s.ErrorBadge
                        title={`${machinesInStatusCount} ${machineLabel} em ${sliceLabel.toLowerCase()}`}
                        style={{ background: sliceColor }}
                      >
                        {machinesInStatusCount}
                      </s.ErrorBadge>
                    </s.CardHeader>
                    <s.CardName>{appliance.description}</s.CardName>
                    {appliance.customerName && (
                      <s.CardCustomer>
                        <img
                          src={icons.ClientPin}
                          alt=""
                          style={{ width: 10, height: 10 }}
                        />
                        {appliance.customerName}
                      </s.CardCustomer>
                    )}
                    <s.BreakdownRow>
                      {BREAKDOWN.map(({ rangeKey, label }) => {
                        const count = Number(
                          (appliance.status && appliance.status[rangeKey]) || 0
                        );
                        if (count === 0) return null;
                        return (
                          <s.BreakdownChip
                            key={rangeKey}
                            title={`${count} ${label}`}
                          >
                            <s.BreakdownDot color={GetColor(label)} />
                            {count}
                          </s.BreakdownChip>
                        );
                      })}
                    </s.BreakdownRow>
                  </s.Card>
                );
              })}
            </s.CardsGrid>
          )}
        </s.Body>
      </s.Panel>
    </s.Backdrop>
  );
};

export default EquipmentDrillDown;
